import { Box, Divider, IconButton, Typography } from "@mui/material";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { useAnimation } from "../contexts/AnimationContext";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LinkedInIcon from "@mui/icons-material/LinkedIn";

function LoginFooter() {
  const ref = useRef(null);
  const dividerRef = useRef(null);
  const { animationStarted } = useAnimation();
  const isInView = useInView(ref, { once: true, margin: "0px" });
  const dividerInView = useInView(dividerRef, { once: true, margin: "0px" });

  const iconButtonStyles = {
    color: "#1a3a52",
    padding: "8px",
    "&:hover": {
      backgroundColor: "rgba(26, 58, 82, 0.08)",
    },
    "&:focus": {
      backgroundColor: "transparent",
    },
  };

  return (
    <Box
      component={motion.footer}
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={
        isInView && animationStarted
          ? { opacity: 1, y: 0 }
          : { opacity: 0, y: 30 }
      }
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      sx={{
        textAlign: "center",
        py: 2,
      }}
    >
      <Divider
        component={motion.hr}
        ref={dividerRef}
        initial={{ opacity: 0, scaleX: 0 }}
        animate={
          dividerInView && animationStarted
            ? { opacity: 0.3, scaleX: 1 }
            : { opacity: 0, scaleX: 0 }
        }
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        sx={{
          my: 2,
          mx: { xs: 4, sm: 6, md: 8 },
          borderColor: "#8b7355",
        }}
      />

      {/* Small/Medium screens: Icon buttons layout */}
      <Box
        sx={{
          display: { xs: "flex", lg: "none" },
          alignItems: "center",
          justifyContent: "space-between",
          px: { xs: 4, sm: 6, md: 8 },
        }}
      >
        {/* xs: Brand name | sm/md: Developer name */}
        <Typography
          sx={{
            fontFamily: '"Open Sans", sans-serif',
            fontSize: { xs: "0.65rem", sm: "0.85rem", md: "0.9rem" },
            fontWeight: { xs: 500, sm: 400 },
            color: "#1a3a52",
            letterSpacing: { xs: "0.5px", sm: "0.3px" },
            opacity: 1,
            flex: "0 0 auto",
          }}
        >
          <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
            &copy; PIXELHARVEST{/*  - SOCIEDADE UNIPESSOAL LDA */}
          </Box>
          <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
            Tiago Neves do Vale
          </Box>
        </Typography>

        <IconButton
          component="a"
          href="tel:+351910060107"
          sx={iconButtonStyles}
        >
          <PhoneIcon sx={{ fontSize: "1rem" }} />
        </IconButton>

        <IconButton
          component="a"
          href="mailto:tiago.vale.dev@gmail.com"
          target="_blank"
          sx={iconButtonStyles}
        >
          <EmailIcon sx={{ fontSize: "1rem" }} />
        </IconButton>

        <IconButton
          component="a"
          href="https://www.linkedin.com/in/tiagonevesdovale"
          target="_blank"
          sx={iconButtonStyles}
        >
          <LinkedInIcon sx={{ fontSize: "1rem" }} />
        </IconButton>

        {/* sm/md: Brand name on the right */}
        <Typography
          sx={{
            fontFamily: '"Open Sans", sans-serif',
            fontSize: { sm: "0.7rem", md: "0.75rem" },
            fontWeight: 500,
            color: "#1a3a52",
            letterSpacing: "0.5px",
            opacity: 1,
            flex: "0 0 auto",
            display: { xs: "none", sm: "block" },
          }}
        >
          &copy; PIXELHARVEST - SOCIEDADE UNIPESSOAL LDA
        </Typography>
      </Box>

      {/* Large screens: Full Footer layout */}
      <Box
        sx={{
          display: { xs: "none", lg: "flex" },
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          px: 9,
        }}
      >
        <Typography
          sx={{
            fontFamily: '"Open Sans", sans-serif',
            fontSize: "0.95rem",
            fontWeight: 400,
            color: "#1a3a52",
            letterSpacing: "0.3px",
          }}
        >
          Tiago Neves do Vale
        </Typography>
        <Box
          component="a"
          href="tel:+351910060107"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textDecoration: "none",
            "&:hover .phone-text": {
              color: "#1a3a52",
              textDecoration: "underline",
            },
          }}
        >
          <PhoneIcon sx={{ fontSize: "1rem", color: "#1a3a52" }} />
          <Typography
            className="phone-text"
            sx={{
              fontFamily: '"Open Sans", sans-serif',
              fontSize: "0.9rem",
              fontWeight: 300,
              color: "#2d4e66",
              letterSpacing: "0px",
            }}
          >
            +351 910 060 107
          </Typography>
        </Box>
        <Box
          component="a"
          href="mailto:tiago.vale.dev@gmail.com"
          target="_blank"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textDecoration: "none",
            "&:hover .email-text": {
              color: "#1a3a52",
              textDecoration: "underline",
            },
          }}
        >
          <EmailIcon sx={{ fontSize: "1rem", color: "#1a3a52" }} />
          <Typography
            className="email-text"
            sx={{
              fontFamily: '"Open Sans", sans-serif',
              fontSize: "0.9rem",
              fontWeight: 300,
              color: "#2d4e66",
              letterSpacing: "0.3px",
            }}
          >
            tiago.vale.dev@gmail.com
          </Typography>
        </Box>
        <Box
          component="a"
          href="https://www.linkedin.com/in/tiagonevesdovale"
          target="_blank"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            textDecoration: "none",
            "&:hover .linkedin-text": {
              color: "#1a3a52",
              textDecoration: "underline",
            },
          }}
        >
          <LinkedInIcon sx={{ fontSize: "1rem", color: "#1a3a52" }} />
          <Typography
            className="linkedin-text"
            sx={{
              fontFamily: '"Open Sans", sans-serif',
              fontSize: "0.9rem",
              fontWeight: 300,
              color: "#2d4e66",
              letterSpacing: "0.3px",
            }}
          >
            linkedin.com/in/tiagonevesdovale
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: '"Open Sans", sans-serif',
            fontSize: "0.8rem",
            fontWeight: 500,
            color: "#1a3a52",
            letterSpacing: "0.5px",
            opacity: 1,
          }}
        >
          &copy; PIXELHARVEST - SOCIEDADE UNIPESSOAL LDA
        </Typography>
      </Box>
    </Box>
  );
}

export default LoginFooter;
